import React, { useEffect, useState } from "react";
import { ArrowLeft, Building, Printer } from "lucide-react";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import CommonDateRangePicker from "../../../Common/CommonDateRangePicker/CommonDateRangePicker";
import { combineAttendance } from "./attendanceUtils";
import { Link, useLocation } from "react-router-dom";

const HRMSAttendance = () => {
  const { state } = useLocation();
  const employees = state?.employees || [];
  const logs = state?.logs || [];
  const rosters = state?.rosters || {};
  const rangeFromParent = state?.range || { startDate: "", endDate: "" };

  const [range, setRange] = useState(rangeFromParent);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!employees || employees.length === 0) return;
    if (!range.startDate || !range.endDate) {
      setAttendance([]);
      return;
    }

    setLoading(true);
    const start = new Date(range.startDate);
    const end = new Date(range.endDate);
    const allDates = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      allDates.push(d.toISOString().split("T")[0]);
    }

    const all = [];
    allDates.forEach((date) => {
      const dayData = combineAttendance(date, employees, logs, rosters);
      if (dayData) all.push(...dayData);
    });

    setAttendance(all);
    setLoading(false);
  }, [range, employees, logs, rosters]);

  const handleDownload = () => {
    if (attendance.length === 0) {
      Swal.fire("No data", "No records available to download", "warning");
      return;
    }

    const ws = XLSX.utils.json_to_sheet(attendance);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");
    XLSX.writeFile(
      wb,
      `Attendance_Report_${range.startDate}_to_${range.endDate}.xlsx`
    );
  };

  if (!state) {
    return (
      <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
        <div className="text-center py-5">
          <h5>
            Please open this report from the <strong>HRMS Reports</strong> page.
          </h5>
          <Link to="/hrms-reports" className="btn btn-primary mt-3">
            Go to Reports
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="row">
        <div className="col-12">
          <div className="hrms-report-dept-cont">
            <div className="d-flex flex-column flex-lg-row row-gap-3 justify-content-between align-items-center">
              <div className="d-flex gap-3 align-items-center">
                <Building size={35} stroke="#302867" />
                <div className="d-flex flex-column">
                  <h4 className="fw-semibold m-0">
                    Attendance Consolidated Report
                  </h4>
                  <span className="text-muted">
                    Consolidated audit report for Attendance.
                  </span>
                </div>
              </div>
              <Link
                to="/hrms-reports"
                className="d-flex align-items-center gap-2 btn hrms-reports-back-btn"
              >
                <ArrowLeft size={20} />
                Back to Reports
              </Link>
            </div>

            <div className="row mt-3 row-gap-2">
              <div className="col-lg-10 col-sm-12 col-12">
                <CommonDateRangePicker
                  label="Date Range"
                  value={range}
                  onChange={setRange}
                />
              </div>
              <div className="col-lg-2 col-sm-12 col-12 d-flex align-items-end">
                <button
                  className="btn hrms-report-btn w-100"
                  onClick={handleDownload}
                >
                  <Printer size={18} stroke="#fff" />
                  <span className="text-light">Download Report</span>
                </button>
              </div>
            </div>
          </div>

          <div className="hrms-leave-report-cont mt-4">
            <div className="d-flex flex-column row-gap-1">
              <h4 className="fw-semibold m-0">
                Filtered Results ({attendance.length})
              </h4>
              <span className="text-muted">
                Displaying records from{" "}
                {range.startDate && range.endDate
                  ? `${range.startDate} to ${range.endDate}`
                  : "No range selected yet"}
              </span>
            </div>

            <div className="table-responsive">
              <table className="table align-middle mb-0 table-hover mt-4">
                <thead>
                  <tr className="text-muted small border-top">
                    <th>Date</th>
                    <th>Employee</th>
                    <th>Emp. Code</th>
                    <th>Department</th>
                    <th>Shift</th>
                    <th>Entry / Exit</th>
                    <th>Worked Hours</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.length > 0 ? (
                    attendance.map((record, i) => (
                      <tr key={i}>
                        <td>{record.date}</td>
                        <td>{record.name || "-"}</td>
                        <td>{record.employee_code || "-"}</td>
                        <td>{record.department || "-"}</td>
                        <td>
                          {record.shiftStart} - {record.shiftEnd}
                        </td>
                        <td>
                          {record.entry} / {record.exit}
                        </td>
                        <td>{record.workedHours}</td>
                        <td>{record.status}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center py-3 text-muted">
                        {range.startDate && range.endDate
                          ? "No attendance history found for the selected criteria."
                          : "Please select a date range."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRMSAttendance;
